import { useEffect, useState } from 'react';
import { healthcareRatesGateway } from './index';
import type { DisciplineCapabilities, EmployeeRateCapabilities, ServiceTypeCapabilities } from './types';

export interface HealthcareCapabilities {
  discipline: DisciplineCapabilities | null;
  serviceType: ServiceTypeCapabilities | null;
  employeeRate: EmployeeRateCapabilities | null;
  loaded: boolean;
}

/**
 * What the signed-in person can do with disciplines and rates, as the three services say it (booleans only). It decides what a screen
 * OFFERS (for example whether "Add a discipline" is shown); it never decides what is allowed: the server still enforces every action and a 403
 * is still handled wherever one can arrive. A capability that cannot be read is `null`, and the screen then offers the action and relies on the
 * server's refusal, exactly as the job-role screens do.
 */
export function useHealthcareCapabilities(): HealthcareCapabilities {
  const [state, setState] = useState<HealthcareCapabilities>({ discipline: null, serviceType: null, employeeRate: null, loaded: false });
  useEffect(() => {
    const controller = new AbortController();
    const settle = <T,>(p: Promise<T>) => p.catch(() => null);
    void Promise.all([
      settle(healthcareRatesGateway.getDisciplineCapabilities(controller.signal)),
      settle(healthcareRatesGateway.getServiceTypeCapabilities(controller.signal)),
      settle(healthcareRatesGateway.getEmployeeRateCapabilities(controller.signal)),
    ]).then(([discipline, serviceType, employeeRate]) => {
      if (!controller.signal.aborted) setState({ discipline, serviceType, employeeRate, loaded: true });
    });
    return () => controller.abort();
  }, []);
  return state;
}
